import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { TimeInput } from '@/components/ui/TimeInput';
import { Select } from '@/components/ui/Select';
import { SlideOver, SlideOverFooter } from '@/components/ui/SlideOver';
import { Textarea } from '@/components/ui/Textarea';
import { TaskAssigneePicker } from '@/components/tasks/TaskAssigneePicker';
import { TaskComments } from '@/components/tasks/TaskComments';
import { TaskRelationPicker } from '@/components/tasks/TaskRelationPicker';
import { useClinicUsersList } from '@/hooks/useClinicUsers';
import {
  useCreateStaffTask,
  useDeleteStaffTask,
  useUpdateStaffTask,
} from '@/hooks/useStaffTasks';
import {
  STAFF_TASK_PRIORITIES,
  STAFF_TASK_STATUSES,
  type StaffTask,
  type StaffTaskLink,
  type StaffTaskPriority,
  type StaffTaskStatus,
} from '@/lib/staffTasks';
import { ConfirmActionModal } from '@/components/patient/PatientTabActions';

export function TaskFormSlideOver({
  clinicId,
  task,
  presetLinks,
  onClose,
}: {
  clinicId: string;
  task?: StaffTask | null;
  presetLinks?: StaffTaskLink[];
  onClose: () => void;
}) {
  const { t } = useTranslation();
  const { data: clinicUsers } = useClinicUsersList(clinicId);
  const createTask = useCreateStaffTask();
  const updateTask = useUpdateStaffTask();
  const deleteTask = useDeleteStaffTask();
  const isEditing = Boolean(task);
  const [confirmDelete, setConfirmDelete] = useState(false);

  const assigneeOptions = useMemo(
    () =>
      (clinicUsers ?? []).map((member) => ({
        user_id: member.user_id,
        name: member.user?.name?.trim() || member.user?.email || member.user_id,
      })),
    [clinicUsers],
  );

  const [title, setTitle] = useState(task?.title ?? '');
  const [description, setDescription] = useState(task?.description ?? '');
  const [status, setStatus] = useState<StaffTaskStatus>(task?.status ?? 'open');
  const [priority, setPriority] = useState<StaffTaskPriority>(task?.priority ?? 'normal');
  const [deadlineDate, setDeadlineDate] = useState(task?.deadline_date ?? '');
  const [deadlineTime, setDeadlineTime] = useState(
    task?.deadline_time ? task.deadline_time.slice(0, 5) : '',
  );
  const [assigneeIds, setAssigneeIds] = useState<string[]>(
    task?.assignees.map((assignee) => assignee.user_id) ?? [],
  );
  const [links, setLinks] = useState<StaffTaskLink[]>(task?.links ?? presetLinks ?? []);

  const dirty =
    title !== (task?.title ?? '') ||
    description !== (task?.description ?? '') ||
    status !== (task?.status ?? 'open') ||
    priority !== (task?.priority ?? 'normal') ||
    deadlineDate !== (task?.deadline_date ?? '') ||
    Boolean(deadlineTime) ||
    assigneeIds.length > 0 ||
    links.length > 0;

  const handleSubmit = async () => {
    const payload = {
      clinic_id: clinicId,
      title: title.trim(),
      description: description.trim() || null,
      status,
      priority,
      deadline_date: deadlineDate || null,
      deadline_time: deadlineDate && deadlineTime ? `${deadlineTime}:00` : null,
      assignee_ids: assigneeIds,
      links,
    };
    try {
      if (isEditing && task) {
        await updateTask.mutateAsync({ ...payload, task_id: task.id });
        toast.success(t('tasks.toast_updated'));
      } else {
        await createTask.mutateAsync(payload);
        toast.success(t('tasks.toast_created'));
      }
      onClose();
    } catch {
      toast.error(isEditing ? t('tasks.error_update') : t('tasks.error_create'));
    }
  };

  const handleDelete = async () => {
    if (!task) return;
    try {
      await deleteTask.mutateAsync({ task_id: task.id, clinic_id: clinicId });
      toast.success(t('tasks.toast_deleted'));
      setConfirmDelete(false);
      onClose();
    } catch {
      toast.error(t('tasks.error_delete'));
    }
  };

  return (
    <>
      <SlideOver
        open
        wide
        title={isEditing ? t('tasks.edit_title') : t('tasks.create_title')}
        onClose={onClose}
        dirty={dirty}
        footer={
          <SlideOverFooter
            onCancel={onClose}
            primary={
              <Button
                disabled={!title.trim() || createTask.isPending || updateTask.isPending}
                loading={createTask.isPending || updateTask.isPending}
                onClick={() => void handleSubmit()}
              >
                {isEditing ? t('common.save') : t('tasks.create')}
              </Button>
            }
          />
        }
      >
        <div className="space-y-s4">
          <Input
            id="staff-task-title"
            required
            label={t('tasks.field_title')}
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            placeholder={t('tasks.placeholder_title')}
          />
          <Textarea
            id="staff-task-description"
            label={t('tasks.field_description')}
            rows={3}
            value={description}
            onChange={(event) => setDescription(event.target.value)}
            placeholder={t('tasks.placeholder_description')}
          />
          <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
            <Select
              id="staff-task-priority"
              label={t('tasks.field_priority')}
              value={priority}
              onChange={(event) => setPriority(event.target.value as StaffTaskPriority)}
            >
              {STAFF_TASK_PRIORITIES.map((value) => (
                <option key={value} value={value}>
                  {t(`tasks.priority.${value}`)}
                </option>
              ))}
            </Select>
            <Select
              id="staff-task-status"
              label={t('tasks.field_status')}
              value={status}
              onChange={(event) => setStatus(event.target.value as StaffTaskStatus)}
            >
              {STAFF_TASK_STATUSES.map((value) => (
                <option key={value} value={value}>
                  {t(`tasks.status.${value}`)}
                </option>
              ))}
            </Select>
          </div>
          <div className="grid grid-cols-1 gap-s4 nav:grid-cols-2">
            <Input
              id="staff-task-deadline-date"
              type="date"
              label={t('tasks.field_deadline_date')}
              value={deadlineDate}
              onChange={(event) => {
                setDeadlineDate(event.target.value);
                if (!event.target.value) setDeadlineTime('');
              }}
            />
            <TimeInput
              id="staff-task-deadline-time"
              label={t('tasks.field_deadline_time')}
              value={deadlineTime}
              disabled={!deadlineDate}
              allowEmpty
              onChange={setDeadlineTime}
            />
          </div>
          <TaskAssigneePicker
            options={assigneeOptions}
            value={assigneeIds}
            onChange={setAssigneeIds}
          />
          <TaskRelationPicker clinicId={clinicId} value={links} onChange={setLinks} />
          {task ? <TaskComments taskId={task.id} clinicId={clinicId} /> : null}
          {task ? (
            <Button type="button" variant="danger" onClick={() => setConfirmDelete(true)}>
              {t('common.delete')}
            </Button>
          ) : null}
        </div>
      </SlideOver>
      <ConfirmActionModal
        open={confirmDelete}
        title={t('tasks.confirm_delete')}
        confirmLabel={t('common.delete')}
        pending={deleteTask.isPending}
        onConfirm={() => void handleDelete()}
        onClose={() => setConfirmDelete(false)}
      />
    </>
  );
}
